import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { X, Camera, Star, ChevronRight, Plus, Image } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { useAuth } from '../../context/AuthContext';
import { API, CIGAR_PLACEHOLDER } from '../../config/constants';
import useModalHistory from '../../hooks/useModalHistory';

const MAX_SCANNER_IMAGE_DIMENSION = 1280;
const SCANNER_JPEG_QUALITY = 0.82;
const DEFAULT_SCAN_CROP = { x: 0.25, y: 0.22, width: 0.5, height: 0.56 };

const clamp = (value, minimum, maximum) => Math.min(Math.max(value, minimum), maximum);

const prepareImageForScanner = (file, cropRect = null) => new Promise((resolve, reject) => {
  const image = new window.Image();
  const objectUrl = URL.createObjectURL(file);

  image.onload = () => {
    URL.revokeObjectURL(objectUrl);
    const sourceWidth = cropRect ? Math.round(image.naturalWidth * cropRect.width) : image.naturalWidth;
    const sourceHeight = cropRect ? Math.round(image.naturalHeight * cropRect.height) : image.naturalHeight;
    const sourceX = cropRect ? Math.round(image.naturalWidth * cropRect.x) : 0;
    const sourceY = cropRect ? Math.round(image.naturalHeight * cropRect.y) : 0;
    const longestSide = Math.max(sourceWidth, sourceHeight);
    const scale = Math.min(1, MAX_SCANNER_IMAGE_DIMENSION / longestSide);
    const width = Math.max(1, Math.round(sourceWidth * scale));
    const height = Math.max(1, Math.round(sourceHeight * scale));
    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    canvas.getContext('2d').drawImage(
      image,
      sourceX,
      sourceY,
      sourceWidth,
      sourceHeight,
      0,
      0,
      width,
      height
    );
    canvas.toBlob((blob) => {
      if (!blob) {
        reject(new Error('Unable to prepare image'));
        return;
      }
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result.split(',')[1]);
      reader.onerror = () => reject(new Error('Unable to read image'));
      reader.readAsDataURL(blob);
    }, 'image/jpeg', SCANNER_JPEG_QUALITY);
  };

  image.onerror = () => {
    URL.revokeObjectURL(objectUrl);
    reject(new Error('Unable to load image'));
  };
  image.src = objectUrl;
});

const CameraModal = ({ onClose }) => {
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [identifying, setIdentifying] = useState(false);
  const [result, setResult] = useState(null);
  const [customCropEnabled, setCustomCropEnabled] = useState(false);
  const [cropRect, setCropRect] = useState(DEFAULT_SCAN_CROP);
  const { token } = useAuth();
  const navigate = useNavigate();
  const { closeModal } = useModalHistory(true, onClose, 'camera-scanner');
  
  // Separate refs for camera and gallery inputs
  const cameraInputRef = useRef(null);
  const galleryInputRef = useRef(null);
  const previewImageRef = useRef(null);
  const cropDragRef = useRef(null);

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setImageFile(file);
      setImagePreview(URL.createObjectURL(file));
      setResult(null);
      setCustomCropEnabled(false);
      setCropRect(DEFAULT_SCAN_CROP);
    }
  };

  const openCamera = () => {
    cameraInputRef.current?.click();
  };

  const openGallery = () => {
    galleryInputRef.current?.click();
  };

  const startCropDrag = (event, mode) => {
    if (!customCropEnabled || !previewImageRef.current) return;
    event.preventDefault();
    event.stopPropagation();
    event.currentTarget.setPointerCapture(event.pointerId);
    cropDragRef.current = {
      pointerId: event.pointerId,
      mode,
      startClientX: event.clientX,
      startClientY: event.clientY,
      startRect: cropRect,
    };
  };

  const moveCrop = (event) => {
    const drag = cropDragRef.current;
    const previewBounds = previewImageRef.current?.getBoundingClientRect();
    if (!drag || drag.pointerId !== event.pointerId || !previewBounds) return;

    const deltaX = (event.clientX - drag.startClientX) / previewBounds.width;
    const deltaY = (event.clientY - drag.startClientY) / previewBounds.height;
    const minimumWidth = 0.15;
    const minimumHeight = 0.15;

    setCropRect(() => {
      if (drag.mode === 'move') {
        return {
          ...drag.startRect,
          x: clamp(drag.startRect.x + deltaX, 0, 1 - drag.startRect.width),
          y: clamp(drag.startRect.y + deltaY, 0, 1 - drag.startRect.height),
        };
      }
      return {
        ...drag.startRect,
        width: clamp(drag.startRect.width + deltaX, minimumWidth, 1 - drag.startRect.x),
        height: clamp(drag.startRect.height + deltaY, minimumHeight, 1 - drag.startRect.y),
      };
    });
  };

  const endCropDrag = (event) => {
    if (cropDragRef.current?.pointerId === event.pointerId) {
      cropDragRef.current = null;
    }
  };

  const handleIdentify = async () => {
    if (!imageFile) return;

    setIdentifying(true);
    try {
      const base64 = await prepareImageForScanner(imageFile, customCropEnabled ? cropRect : null);
      const res = await axios.post(
        `${API}/cigars/identify`,
        { image_base64: base64 },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setResult(res.data);
      if (res.data.already_exists) {
        toast.success('Cigar found in database!');
      }
    } catch (error) {
      const message = error.response?.status === 413
        ? 'Photo is too large. Try a closer, cropped photo of the cigar band.'
        : error.response?.data?.detail || 'Failed to identify cigar';
      toast.error(message);
    } finally {
      setIdentifying(false);
    }
  };

  const handleViewCigar = (cigarId) => {
    onClose();
    navigate(`/cigar/${cigarId}`);
  };

  const [addingCigar, setAddingCigar] = useState(false);

  const handleAddNewCigar = async () => {
    if (!result?.parsed_data) return;
    
    setAddingCigar(true);
    try {
      // Prepare payload with cigar data and the scanned image
      const payload = {
        ...result.parsed_data
      };
      
      // If we have an image file, convert it to base64 and include it
      if (imageFile) {
        payload.scanned_image_base64 = await prepareImageForScanner(imageFile);
      }
      
      const res = await axios.post(
        `${API}/cigars/confirm-add`,
        payload,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`"${result.parsed_data.brand} ${result.parsed_data.name}" added successfully!`);
      onClose();
      navigate(`/cigar/${res.data.id}`);
    } catch (error) {
      if (error.response?.status === 400) {
        toast.error(error.response.data.detail || 'This cigar already exists');
      } else {
        toast.error('Failed to add cigar');
      }
    } finally {
      setAddingCigar(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/90 backdrop-blur-sm z-50 flex items-start justify-center p-4 overflow-y-auto">
      <div className="bg-[#141416] rounded-2xl max-w-md w-full border border-white/10 my-4">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-white/10">
          <div className="flex items-center gap-2">
            <Camera className="text-amber-500" size={24} />
            <h2 className="text-xl font-bold text-white">AI Cigar Scanner</h2>
          </div>
          <button data-testid="close-camera-button" onClick={closeModal}>
            <X className="text-white/70 hover:text-white" size={24} />
          </button>
        </div>

        <div className="p-4 max-h-[80vh] overflow-y-auto">
          {/* Hidden file inputs */}
          <input
            ref={cameraInputRef}
            data-testid="camera-file-input"
            type="file"
            accept="image/*"
            capture="environment"
            onChange={handleFileChange}
            className="hidden"
          />
          <input
            ref={galleryInputRef}
            data-testid="gallery-file-input"
            type="file"
            accept="image/*"
            onChange={handleFileChange}
            className="hidden"
          />

          {/* Image Upload Area */}
          {!imagePreview ? (
            <div className="space-y-3">
              {/* Camera option */}
              <button
                data-testid="camera-capture-button"
                onClick={openCamera}
                className="flex flex-col items-center justify-center w-full h-36 border-2 border-dashed border-white/20 rounded-xl cursor-pointer hover:border-amber-500/50 transition-colors"
              >
                <Camera className="text-amber-500 mb-2" size={40} />
                <span className="text-white/70 text-sm font-medium">Take Photo</span>
                <span className="text-white/40 text-xs">Use your camera</span>
              </button>
              
              {/* Gallery option */}
              <button
                data-testid="gallery-upload-button"
                onClick={openGallery}
                className="flex flex-col items-center justify-center w-full h-36 border-2 border-dashed border-white/20 rounded-xl cursor-pointer hover:border-amber-500/50 transition-colors"
              >
                <Image className="text-amber-500 mb-2" size={40} />
                <span className="text-white/70 text-sm font-medium">Upload from Gallery</span>
                <span className="text-white/40 text-xs">Choose existing photo</span>
              </button>
            </div>
          ) : (
            !result && imagePreview && (
              <div className="relative">
                <img
                  ref={previewImageRef}
                  src={imagePreview}
                  alt="Preview"
                  className="w-full max-h-64 object-contain rounded-xl bg-black/20"
                />
                {customCropEnabled && (
                  <div
                    data-testid="custom-scan-crop-frame"
                    role="group"
                    aria-label="Draggable scan crop area"
                    onPointerDown={(event) => startCropDrag(event, 'move')}
                    onPointerMove={moveCrop}
                    onPointerUp={endCropDrag}
                    onPointerCancel={endCropDrag}
                    style={{
                      left: `${cropRect.x * 100}%`,
                      top: `${cropRect.y * 100}%`,
                      width: `${cropRect.width * 100}%`,
                      height: `${cropRect.height * 100}%`,
                    }}
                    className="absolute border-2 border-amber-400 rounded-lg cursor-move touch-none shadow-[0_0_0_999px_rgba(0,0,0,0.24)]"
                  >
                    <span className="absolute -top-6 left-0 rounded bg-amber-500 px-1.5 py-0.5 text-[10px] font-bold text-black">Drag to position</span>
                    <button
                      type="button"
                      data-testid="custom-scan-crop-resize-handle"
                      aria-label="Resize scan crop area"
                      onPointerDown={(event) => startCropDrag(event, 'resize')}
                      className="absolute -bottom-2 -right-2 h-5 w-5 rounded-full border-2 border-white bg-amber-500 cursor-se-resize"
                    />
                  </div>
                )}
                <button
                  data-testid="remove-scanner-image-button"
                  onClick={() => {
                    setImageFile(null);
                    setImagePreview(null);
                    setResult(null);
                    setCustomCropEnabled(false);
                    setCropRect(DEFAULT_SCAN_CROP);
                    // Reset file inputs so the same file can be selected again
                    if (cameraInputRef.current) cameraInputRef.current.value = '';
                    if (galleryInputRef.current) galleryInputRef.current.value = '';
                  }}
                  className="absolute top-2 right-2 bg-black/50 rounded-full p-1"
                >
                  <X className="text-white" size={20} />
                </button>
              </div>
            )
          )}

          {imagePreview && !result && (
            <div className="mt-3 rounded-xl border border-white/10 bg-white/[0.03] p-3">
              <div className="flex items-center justify-between gap-3">
                <div>
                  <p className="text-sm font-medium text-white">Custom band crop</p>
                  <p className="text-xs text-white/45 mt-0.5">Drag and resize the frame around any band before scanning.</p>
                </div>
                <button
                  type="button"
                  data-testid="custom-scan-crop-toggle"
                  aria-pressed={customCropEnabled}
                  onClick={() => setCustomCropEnabled((current) => !current)}
                  className={`min-w-14 px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors ${customCropEnabled ? 'bg-amber-600 text-white' : 'bg-white/10 text-white/65 hover:text-white'}`}
                >
                  {customCropEnabled ? 'On' : 'Off'}
                </button>
              </div>
              {customCropEnabled && (
                <div className="mt-3 flex items-center justify-between gap-3">
                  <p data-testid="custom-scan-crop-active" className="text-amber-400 text-xs">
                    Only the selected area is sent to AI; the complete photo is saved when you add the cigar.
                  </p>
                  <button
                    type="button"
                    data-testid="custom-scan-crop-reset-button"
                    onClick={() => setCropRect(DEFAULT_SCAN_CROP)}
                    className="text-xs text-white/60 hover:text-white transition-colors"
                  >
                    Reset
                  </button>
                </div>
              )}
            </div>
          )}

          {/* Identify Button */}
          {imagePreview && !result && (
            <Button
              data-testid="identify-button"
              onClick={handleIdentify}
              disabled={identifying}
              className="w-full mt-4 bg-gradient-to-br from-amber-600 to-amber-800 hover:from-amber-500 hover:to-amber-700 py-6 rounded-xl"
            >
              {identifying ? (
                <span className="flex items-center gap-2">
                  <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  Analyzing...
                </span>
              ) : (
                'Identify This Cigar'
              )}
            </Button>
          )}

          {/* Results */}
          {result && (
            <div className="space-y-4">
              {/* Show uploaded image in results */}
              {imagePreview && (
                <div className="flex justify-center bg-black/20 rounded-xl p-2">
                  <img
                    src={imagePreview}
                    alt="Uploaded cigar"
                    className="max-w-full rounded-lg"
                    style={{ maxHeight: '300px', objectFit: 'contain' }}
                  />
                </div>
              )}
              
              {/* AI Analysis */}
              {result.parsed_data && (
                <div className="bg-white/5 rounded-xl p-4">
                  <h3 className="text-amber-500 font-bold mb-2">AI Analysis</h3>
                  <div className="space-y-1 text-sm">
                    {result.parsed_data.brand && (
                      <p className="text-white"><span className="text-white/50">Brand:</span> {result.parsed_data.brand}</p>
                    )}
                    {result.parsed_data.name && (
                      <p className="text-white"><span className="text-white/50">Name:</span> {result.parsed_data.name}</p>
                    )}
                    {result.parsed_data.strength && (
                      <p className="text-white"><span className="text-white/50">Strength:</span> {result.parsed_data.strength}</p>
                    )}
                    {result.parsed_data.origin && (
                      <p className="text-white"><span className="text-white/50">Origin:</span> {result.parsed_data.origin}</p>
                    )}
                    {result.parsed_data.wrapper && (
                      <p className="text-white"><span className="text-white/50">Wrapper:</span> {result.parsed_data.wrapper}</p>
                    )}
                    {result.parsed_data.size && (
                      <p className="text-white"><span className="text-white/50">Size:</span> {result.parsed_data.size}</p>
                    )}
                    {result.parsed_data.flavor_notes?.length > 0 && (
                      <div className="flex flex-wrap gap-1 mt-2">
                        {result.parsed_data.flavor_notes.map((note, idx) => (
                          <span key={idx} className="bg-amber-500/20 text-amber-300 text-xs px-2 py-0.5 rounded-full">
                            {note}
                          </span>
                        ))}
                      </div>
                    )}
                    {result.parsed_data.confidence && (
                      <p className="text-white/50 text-xs mt-2">
                        Confidence: {result.parsed_data.confidence}
                      </p>
                    )}
                  </div>
                </div>
              )}

              {/* Description */}
              {result.identification && !result.parsed_data && (
                <div className="bg-white/5 rounded-xl p-4">
                  <p className="text-white text-sm">{result.identification}</p>
                </div>
              )}

              {/* Already Exists Message */}
              {result.already_exists && (
                <div className="space-y-3">
                  <div className="bg-green-500/10 border border-green-500/30 rounded-xl p-3">
                    <p className="text-green-400 text-sm">
                      ✓ This cigar is already in the database!
                    </p>
                  </div>
                  <button
                    onClick={() => handleViewCigar(result.already_exists.id)}
                    className="w-full flex items-center gap-3 bg-white/5 border border-green-500/30 rounded-xl p-3 hover:bg-white/10 transition-colors text-left"
                  >
                    <div className="w-12 h-12 bg-white/10 rounded-lg overflow-hidden flex-shrink-0">
                      <img 
                        src={result.already_exists.image_url || CIGAR_PLACEHOLDER} 
                        alt={result.already_exists.name}
                        className="w-full h-full object-cover"
                      />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-amber-500 text-xs">{result.already_exists.brand}</p>
                      <p className="text-white font-medium truncate">{result.already_exists.name}</p>
                      <div className="flex items-center gap-1">
                        <Star className="text-amber-500 fill-amber-500" size={12} />
                        <span className="text-amber-500 text-xs">{result.already_exists.average_rating?.toFixed(1) || '0.0'}</span>
                      </div>
                    </div>
                    <ChevronRight className="text-white/30" size={20} />
                  </button>
                </div>
              )}

              {/* Not in Database - Can Add */}
              {result.parsed_data && !result.already_exists && (
                <div className="space-y-3">
                  {/* Add This Cigar Button - Always show first when cigar not in DB */}
                  <Button
                    onClick={handleAddNewCigar}
                    disabled={addingCigar}
                    className="w-full bg-gradient-to-br from-amber-600 to-amber-800 hover:from-amber-500 hover:to-amber-700 text-white py-4 rounded-xl disabled:opacity-50"
                  >
                    {addingCigar ? (
                      <>
                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                        Adding...
                      </>
                    ) : (
                      <>
                        <Plus size={18} className="mr-2" />
                        Add This Cigar
                      </>
                    )}
                  </Button>

                  {/* "Similar Cigars" - name-based variants */}
                  {result.suggestions && result.suggestions.length > 0 && (
                    <>
                      <div className="bg-white/5 border border-white/10 rounded-xl p-3">
                        <p className="text-white/70 text-sm font-medium">
                          Similar Cigars
                        </p>
                      </div>
                      <div className="space-y-2">
                        {result.suggestions.map((cigar, index) => (
                          <div
                            key={index}
                            className="w-full flex items-center gap-3 bg-white/5 border border-white/10 rounded-xl p-3"
                          >
                            <div className="flex-1 min-w-0">
                              <p className="text-amber-500 text-xs">{cigar.brand}</p>
                              <p className="text-white font-medium text-sm">{cigar.name}</p>
                            </div>
                          </div>
                        ))}
                      </div>
                    </>
                  )}
                </div>
              )}

              {/* Scan Another */}
              <Button
                onClick={() => {
                  setImageFile(null);
                  setImagePreview(null);
                  setResult(null);
                }}
                variant="outline"
                className="w-full border-white/20 text-white hover:bg-white/10 py-4 rounded-xl"
              >
                Scan Another Cigar
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CameraModal;
